import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { AccessConfirmationData } from './Security';
import { composeFullname, composeUsername } from '../../../lib/DisplayName';
import {
  ContactSchema,
  RoomSecuritySchema,
  UserSchema,
} from '../../../definitions/schemas';
import { handleAutofocus } from '../../../lib/TextField';
import { OrganizationFlagEnum } from '../../../lib/features/FeatureFlag';
import { RoomType } from '../../../enums/RoomTypes';
import { useI18n } from '../../../lib/hook/useI18n';
import FormValidatorHelper from '../../../Services/FormValidatorService';
import PrivacyPolicy from './PrivacyPolicy';
import PrivacyPolicyLink from '../../ConsentPromp/PrivacyPolicyLink';
import useFeatureFlag from '../../../lib/hook/useFeatureFlag';

type PickedRoomSecurityProps = Pick<
  RoomSecuritySchema,
  'invitedContactsOnly' | 'requiresEmail' | 'requiresPasscode' | 'verifyEmail'
>;

interface Props extends PickedRoomSecurityProps {
  contact?: ContactSchema;
  displayConsent?: boolean;
  email?: string;
  hasError: boolean;
  loading: boolean;
  onSubmit: (data: AccessConfirmationData) => void;
  open: boolean;
  owner?: UserSchema;
  roomType?: string;
}

const SecurityDialog: React.FC<Props> = (props: Props): JSX.Element => {
  const { i18nClasses, t, tc } = useI18n();

  const isInhouseConsentEnabled = useFeatureFlag(
    OrganizationFlagEnum.INHOUSE_CONSENT
  );

  const {
    contact,
    displayConsent,
    email: initialEmail,
    hasError,
    invitedContactsOnly,
    loading,
    onSubmit,
    open,
    owner,
    requiresEmail,
    requiresPasscode,
    roomType,
    verifyEmail,
  } = props;

  const [email, setEmail] = useState(initialEmail ?? '');
  const [passcode, setPasscode] = useState('');

  const [dirty, setDirty] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validator = new FormValidatorHelper();
  const isContentLink = roomType === RoomType.ContentLink;

  const shouldVerifyEmail = (): boolean =>
    invitedContactsOnly || requiresEmail || verifyEmail;

  const getRequiredFields = (): string[] => {
    const requiredFields: string[] = [];

    if (shouldVerifyEmail()) {
      requiredFields.push(t('room.welcome.modal.email').toLowerCase());
    }

    if (requiresPasscode) {
      requiredFields.push(t('room.welcome.modal.passcode').toLowerCase());
    }

    return requiredFields;
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setDirty(true);
  };

  const onPasscodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasscode(e.target.value.trim());
    setDirty(true);
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    setSubmitted(true);

    if (!validator.isValid()) {
      return;
    }

    const data: AccessConfirmationData = {};

    if (shouldVerifyEmail()) {
      data.email = email;
    }

    if (requiresPasscode) {
      data.passcode = passcode;
    }

    onSubmit(data);
  };

  const renderTitle = (): React.ReactNode => {
    let title = t('room.welcome.modal.title');
    if (isContentLink && owner) {
      title = `${composeUsername(owner)} shared this content with you`;
    } else if (contact?.firstName) {
      title = t('room.welcome.modal.hi_contact', {
        contact: composeFullname(contact),
      });
    }
    return title;
  };

  const renderSubtitle = (): JSX.Element => {
    const fields = getRequiredFields().join(` ${t('room.welcome.modal.and')} `);

    if (verifyEmail) {
      return (
        <Typography color="textSecondary">
          {t('room.pin.modal.body')}
        </Typography>
      );
    }
    return (
      <Typography color="textSecondary">
        {t('room.welcome.modal.body', { fields })}
      </Typography>
    );
  };

  const renderErrorMessage = (): JSX.Element | undefined => {
    const fields = getRequiredFields().join(` ${t('room.welcome.modal.or')} `);
    const validateOnlyEmail = !requiresPasscode && shouldVerifyEmail();
    return (
      <Collapse in={hasError}>
        <Alert severity="error">
          {validateOnlyEmail
            ? t('room.welcome.modal.validation.only_email')
            : t('room.welcome.modal.validation.error_messages', {
                fields,
              })}
        </Alert>
      </Collapse>
    );
  };

  const renderEmail = (): JSX.Element | undefined => {
    if (!shouldVerifyEmail()) {
      return;
    }

    const helperText = verifyEmail ? t('room.pin.modal.helptext') : '';
    const validatorKey = t('room.welcome.modal.email').toLowerCase();
    validator.check(validatorKey, email, 'required|email');
    const error = validator.getError(validatorKey, submitted);

    return (
      <Box pt={2}>
        <TextField
          color="secondary"
          error={!!error}
          fullWidth
          helperText={error || helperText}
          inputRef={handleAutofocus(!email, dirty)}
          label={t('room.welcome.modal.email')}
          onChange={onEmailChange}
          required
          type="email"
          value={email}
          variant="outlined"
        />
      </Box>
    );
  };

  const renderPasscode = (): JSX.Element | undefined => {
    if (!requiresPasscode) {
      return;
    }

    const validatorKey = t('room.welcome.modal.passcode')
      .toString()
      .toLowerCase();
    validator.check(validatorKey, passcode, 'required');
    const error = validator.getError(validatorKey, submitted);

    return (
      <Box pt={2}>
        <TextField
          autoFocus={!!email}
          color="secondary"
          error={!!error}
          fullWidth
          helperText={error}
          inputRef={handleAutofocus(!passcode && !shouldVerifyEmail(), dirty)}
          label={t('room.welcome.modal.passcode')}
          onChange={onPasscodeChange}
          required
          value={passcode}
          variant="outlined"
        />
      </Box>
    );
  };

  const renderButtonText = () => {
    if (verifyEmail) {
      return t('room.pin.modal.button');
    }

    if (isContentLink) {
      return 'view content';
    }

    return t('room.welcome.modal.button');
  };

  const renderPrivacyPolicy = (): JSX.Element | undefined => {
    if (!shouldVerifyEmail() || isInhouseConsentEnabled) {
      return;
    }

    return <PrivacyPolicy />;
  };

  const renderConsent = (): JSX.Element | undefined => {
    if (!displayConsent || !shouldVerifyEmail() || !isInhouseConsentEnabled) {
      return;
    }

    return (
      <Box pt={2}>
        <Typography
          className={i18nClasses.root}
          color="textSecondary"
          variant="subtitle2"
        >
          {tc(
            'room.welcome.modal.consent',
            { actionText: renderButtonText() },
            {
              privacyPolicyLink: (
                <PrivacyPolicyLink color="textSecondary" variant="subtitle2" />
              ),
            },
            true
          )}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      BackdropProps={{
        style: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'var(--dialog-background-color-dark)',
        },
      }}
      fullWidth
      maxWidth="xs"
      open={open}
    >
      {renderErrorMessage()}
      <DialogTitle>{renderTitle()}</DialogTitle>
      <form onSubmit={handleSubmit} tabIndex={-1} noValidate>
        <DialogContent>
          {renderSubtitle()}
          {renderEmail()}
          {renderPasscode()}
          {renderConsent()}
          {renderPrivacyPolicy()}
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            disabled={loading}
            type="submit"
            variant="contained"
          >
            {renderButtonText()}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SecurityDialog;
